<template>
  <div>
    <Head title="Clientes" />
    <!-- Header y Barra de herramientas -->
    <app-toolbar 
      name="Cliente" 
      namePlural="Clientes" 
      createHref="/clients/create" 
      :exportButtons="true"
    >
      <!-- Filtros de búsqueda -->
      <template v-slot:search>
        <search-filter 
          v-model="form.search" 
          class="mr-4 w-full max-w-md" 
          @reset="reset"
        >
          <label class="block text-gray-700 mb-1">Estado:</label>
          <select 
            v-model="form.trashed" 
            class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          >
            <option :value="null">Todos</option>
            <option value="with">Con eliminados</option>
            <option value="only">Solo eliminados</option>
          </select>
        </search-filter>
      </template>

      <!-- Botones de exportación -->
      <template v-slot:exportArea>
        <!-- Estado de cuenta online -->
        <div class="flex flex-wrap items-center gap-2">
          <a class="bg-black text-white rounded-lg p-3 relative group hover:bg-gray-800 flex items-center gap-2 min-w-[120px] justify-center" 
            target="_blank"
            :href="`/public/client?account_identification=${account.identification}`"
            title="Este es un enlace que te permite darle a tus clientes la posibilidad de ver sus estados de cuenta, los pagos realizados y los pedidos realizados">
            <i class="fa fa-users"></i>
            <span>Link</span>
            <span class="bg-blue-300 text-blue-600 px-2 py-1 rounded-lg font-bold text-xs">BETA</span>
            <!-- Tooltip -->
            <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-64 text-center z-50 pointer-events-none">
              Este es un enlace que te permite darle a tus clientes la posibilidad de ver sus estados de cuenta, los pagos realizados y los pedidos realizados
              <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-8 border-transparent border-t-gray-900"></div>
            </div>
          </a>
          <a 
            target="_blank" 
            href="/clientsReports/excel"
            class="p-3 bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2 min-w-[120px] justify-center"
            title="Exportar a Excel"
          >
            <i class="fa fa-file text-white" />
            <span class="text-white">Excel</span>
          </a>
          <a 
            target="_blank" 
            href="/clientsReports/pdf"
            class="p-3 bg-red-700 hover:bg-red-800 rounded-lg transition-colors flex items-center gap-2 min-w-[120px] justify-center"
            title="Exportar a PDF"
          >
            <i class="fa fa-print text-white" />
            <span class="text-white">PDF</span>
          </a>
          <!-- Botón de importar -->
          <div class="relative">
            <button 
              class="p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 min-w-[120px] justify-center"
              :class="{'bg-blue-700': imported}"
              @click="toggleImport"
              title="Importar clientes"
              type="button"
            >
              <i class="fa fa-upload text-white"></i>
              <span class="text-white">Importar</span>
            </button>
            <form 
              v-if="imported"
              class="absolute top-full right-0 mt-2 p-4 bg-white rounded-lg shadow-lg z-50 w-[90vw] sm:w-[400px]"
              action="/clients/import" 
              method="post"
              enctype="multipart/form-data"
            >
              <input name="_token" type="hidden" :value="csrf" />
              <div class="flex flex-col gap-3">
                <input 
                  type="file" 
                  name="file"
                  class="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
                  accept=".csv,.xlsx"
                />
                <button type="submit" class="btn-blue w-full">
                  Subir archivo
                </button>
              </div>
            </form>
          </div>
        </div>
      </template>
    </app-toolbar>

    <!-- Tabla de clientes -->
    <div class="bg-white rounded-lg shadow-sm overflow-x-auto">
      <!-- Vista móvil -->
      <div class="sm:hidden">
        <div v-for="client in clients.data" 
          :key="client.id" 
          class="p-4 border-b"
          :class="{'bg-red-100': client.balance > 0}"
        >
          <Link 
            :href="`/clients/${client.id}`"
            class="block space-y-2"
          >
            <div class="flex justify-between items-start">
              <div>
                <h3 class="font-semibold text-lg">{{ client.companyName }}</h3>
                <p class="text-gray-600">{{ client.name }}</p>
              </div>
              <icon 
                v-if="client.deleted_at" 
                name="trash" 
                class="w-4 h-4 text-gray-400"
                title="Cliente eliminado"
              />
            </div>
            <div class="text-sm text-gray-500 space-y-1">
              <p><i class="fa fa-id-card mr-2"></i>{{ client.identification }}</p>
              <p><i class="fa fa-envelope mr-2"></i>{{ client.email }}</p>
              <p><i class="fa fa-map-marker-alt mr-2"></i>{{ client.address }}</p>
              <p><i class="fa fa-phone mr-2"></i>{{ client.phone }}</p>
              <p><i class="fa fa-calendar mr-2"></i>Última compra: {{ client.last_purchase_date ? formatDate(client.last_purchase_date) : 'Nunca' }}</p>
            </div>
            <div class="flex justify-between items-center pt-2">
              <span class="font-semibold" :class="{'text-red-600': client.balance > 0}">
                {{ formatCurrency(client.balance) }}
              </span>
              <i class="fa fa-chevron-right text-gray-400"></i>
            </div>
          </Link>
        </div>
      </div>

      <!-- Vista desktop -->
      <table class="hidden sm:table w-full whitespace-nowrap">
        <thead>
          <tr class="text-left bg-gray-50">
            <th class="py-4 px-6 font-semibold">Empresa</th>
            <th class="py-4 px-6 font-semibold">Nombre</th>
            <th class="py-4 px-6 font-semibold">Alias o Cédula</th>
            <th class="py-4 px-6 font-semibold">Último pago</th>
            <th class="py-4 px-6 font-semibold">Última compra</th>
            <th class="py-4 px-6 font-semibold" colspan="2">Adeudado</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="client in clients.data" 
            :key="client.id" 
            :class="{
              'hover:bg-gray-50 focus-within:bg-gray-50': true,
              'bg-red-100': client.balance > 0
            }"
          >
            <td class="border-t">
              <Link 
                class="block px-6 py-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                {{ client.companyName }}
              </Link>
            </td>
            <td class="border-t">
              <Link 
                class="flex items-center px-6 py-4 hover:text-blue-600 focus:text-blue-600 transition-colors" 
                :href="`/clients/${client.id}`"
              >
                {{ client.name }}
                <icon 
                  v-if="client.deleted_at" 
                  name="trash" 
                  class="ml-2 w-4 h-4 text-gray-400"
                  title="Cliente eliminado"
                />
              </Link>
            </td>
            <td class="border-t">
              <Link 
                class="block px-6 py-4 hover:text-blue-600" 
                :href="`/clients/${client.id}`"
              >
                {{ client.identification }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="block px-6 py-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                {{ client.last_payment_date ? formatDate(client.last_payment_date) : 'Nunca' }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="block px-6 py-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                {{ client.last_purchase_date ? formatDate(client.last_purchase_date) : 'Nunca' }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="block px-6 py-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                {{ client.balance }}
              </Link>
            </td>
            <td class="w-px border-t">
              <Link 
                class="block p-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                <icon 
                  name="cheveron-right" 
                  class="w-6 h-6 text-gray-400"
                />
              </Link>
            </td>
          </tr>

          <tr v-if="clients.data.length === 0">
            <td 
              class="px-6 py-8 text-center text-gray-500 border-t" 
              colspan="10"
            >
              No hay clientes disponibles
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination 
      v-if="clients.links" 
      :links="clients.links" 
      class="mt-6"
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import AppToolbar from '../../Shared/AppToolbar.vue'

export default {
  name: 'ClientsIndex',

  components: {
    Head,
    Icon,
    Link,
    Pagination,
    SearchFilter,
    AppToolbar,
  },

  layout: Layout,

  props: {
    filters: Object,
    clients: Object,
    account: Object,
    csrf: { // Asegúrate de pasar este prop desde el backend
      type: String,
      required: false
    }
  },

  data() {
    return {
      form: {
        search: this.filters.search,
        trashed: this.filters.trashed,
      },
      imported: false // Nuevo estado para mostrar el formulario
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get('/clients', pickBy(this.form), { 
          preserveState: true 
        })
      }, 300),
    },
  },

  methods: {
    back() {
      window.history.back()
    },

    reset() {
      this.form = mapValues(this.form, () => null)
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP'
      }).format(value)
    },

    formatDate(date) {
      if (!date) return 'Nunca'
      return new Date(date).toLocaleDateString('es-VE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      })
    },
    toggleImport() {
      this.imported = !this.imported
    },
  },
}
</script>
